<template>
  <div class="notifications-page">
    <div class="notifications-page__header">
      <div class="notifications-page__title">
        {{ $t('title') }}
      </div>
      <button
        type="button"
        class="notifications-page__settings-btn"
        :title="$t('settingsTitle')"
        @click="goToNotificationSettings"
      >
        <img :src="settingsIcon" alt="settings" />
      </button>
    </div>

    <div class="notifications-page__body">
      <VProgressLinear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="notifications-page__loader"
      />

      <div
        v-else-if="!displayNotifications.length"
        class="notifications-page__empty"
      >
        {{ $t('empty') }}
      </div>

      <NotificationsList
        v-else
        :notifications="displayNotifications"
        :is-loading-more="isLoadingMore"
      />
    </div>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref } from '@vue/composition-api';
import { accountTabs, settingsTabs } from '@/enums';
import NotificationsList from '@/components/Notifications/NotificationsList.vue';
import useSystemNotifications from '@/compositions/useSystemNotifications';

export default {
  name: 'NotificationsPage',
  components: {
    NotificationsList,
  },
  setup() {
    const {
      displayNotifications,
      hasMore,
      isLoading,
      isLoadingMore,
      initializePage,
      loadMore,
      finalizeNotificationsPage,
    } = useSystemNotifications();

    const isFinalizing = ref(false);

    function finalizeOnceInBackground() {
      if (isFinalizing.value) {
        return;
      }

      isFinalizing.value = true;

      finalizeNotificationsPage()
        .catch(() => {})
        .finally(() => {
          isFinalizing.value = false;
        });
    }

    function handleScroll() {
      if (isLoading.value || isLoadingMore.value || !hasMore.value) {
        return;
      }

      const scrollBottom = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.scrollHeight - 200;

      if (scrollBottom >= threshold) {
        loadMore().catch(() => {});
      }
    }

    onMounted(async () => {
      window.addEventListener('scroll', handleScroll, { passive: true });

      await initializePage();
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll);
      finalizeOnceInBackground();
    });

    return {
      displayNotifications,
      isLoading,
      isLoadingMore,
      loadMore,
    };
  },
  metaInfo() {
    return {
      title: this.$t('title'),
    };
  },
  computed: {
    settingsIcon() {
      return require('@/assets/img/notifications/notificationSettings.svg');
    },
  },
  methods: {
    goToNotificationSettings() {
      this.$router.push({
        name: 'settings',
        params: {
          type: settingsTabs.ACCOUNT,
          addType: accountTabs.SETTINGS,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.notifications-page {
  width: 100%;
  padding: 40px 20px 0;
}

.notifications-page__header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notifications-page__title {
  color: #212121;
  font-size: 20px;
  font-weight: 400;
  line-height: 100%;
}

.notifications-page__settings-btn {
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 16px;
    height: 16px;
    display: block;
  }
}

.notifications-page__body {
  margin-top: 20px;
}

.notifications-page__loader {
  max-width: 620px;
}

.notifications-page__empty {
  max-width: 620px;
  color: #757575;
  font-size: 14px;
  line-height: 18px;
}

@media screen and (max-width: 960px) {
  .notifications-page {
    padding: 28px 12px 0;
  }

  .notifications-page__body {
    margin-top: 18px;
  }
}

@media screen and (max-width: 600px) {
  .notifications-page {
    padding: 20px 8px 0;
  }

  .notifications-page__body {
    margin-top: 16px;
  }
}
</style>

<i18n locale="en" lang="yaml">
  title: Notification center
  settingsTitle: Notification settings
  empty: No notifications yet
</i18n>

<i18n locale="ru" lang="yaml">
  title: Центр уведомлений
  settingsTitle: Настройки уведомлений
  empty: Уведомлений пока нет
</i18n>
